import { useMemo } from 'react';
import { motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { prizeLabel } from '@/features/spin/prize-label.js';
import type { SpinPrize } from '@/features/spin/api.js';

interface Props {
  prizes: SpinPrize[];
  /** Target rotation in degrees; only ever increases. Framer animates to it. */
  rotationDeg: number;
  spinning: boolean;
  size?: number;
}

// Casino palette — alternating so adjacent segments contrast.
export const WHEEL_COLORS = ['#d4af37', '#b91c1c', '#1e3a8a', '#15803d', '#7c3aed', '#c2410c', '#0e7490', '#be185d'];

export function SpinWheel({ prizes, rotationDeg, spinning, size = 240 }: Props) {
  const { t } = useTranslation('spin');
  const seg = prizes.length || 1;
  const slice = 360 / seg;
  const gradient = useMemo(() => {
    if (prizes.length <= 1) return WHEEL_COLORS[0];
    return prizes.map((_, i) => `${WHEEL_COLORS[i % WHEEL_COLORS.length]} ${i * slice}deg ${(i + 1) * slice}deg`).join(', ');
  }, [prizes, slice]);

  // Where the artwork sits: far enough out to clear the hub (0.1r), far enough
  // in that a rotated square never crosses the rim.
  const artRadius = size * 0.32;
  // ...and how big, capped by the wedge's own arc so a many-prize wheel does not
  // stack its pictures on top of each other.
  const artSize = Math.max(14, Math.min(size * 0.17, (2 * Math.PI * artRadius * 0.72) / seg));

  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      {/* pointer */}
      <div
        className="absolute left-1/2 -top-1 -translate-x-1/2 z-20"
        style={{
          width: 0,
          height: 0,
          borderLeft: '13px solid transparent',
          borderRight: '13px solid transparent',
          borderTop: '22px solid #f5c518',
          filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.4))',
        }}
        aria-hidden
      />
      {/* wheel — Framer Motion drives the rotation (JS-animated, reliable). */}
      <motion.div
        className="relative rounded-full"
        style={{
          width: size,
          height: size,
          background: prizes.length > 1 ? `conic-gradient(${gradient})` : (gradient as string),
          boxShadow: '0 0 0 6px var(--bg-elevated), 0 0 0 8px rgba(255,255,255,0.08), 0 8px 24px rgba(0,0,0,0.35)',
        }}
        animate={{ rotate: rotationDeg }}
        transition={{ duration: spinning ? 4 : 0, ease: [0.17, 0.67, 0.12, 0.99] }}
      >
        {/* Prize artwork — a child of the rotating wheel so it stays on its own
            wedge, which a conic-gradient stop could never carry. Optional: a
            prize without one leaves its wedge exactly as it was. */}
        {prizes.map((p, i) =>
          p.imageUrl ? (
            <img
              key={`${p.key}-${i}`}
              src={p.imageUrl}
              alt={prizeLabel(p, t)}
              className="pointer-events-none absolute left-1/2 top-1/2 rounded-md object-cover"
              style={{
                width: artSize,
                height: artSize,
                marginLeft: -artSize / 2,
                marginTop: -artSize / 2,
                transform: `rotate(${i * slice + slice / 2}deg) translateY(${-artRadius}px)`,
              }}
            />
          ) : null,
        )}
      </motion.div>
      {/* hub */}
      <div
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[var(--bg-raised)] z-20 grid place-items-center"
        style={{ width: size * 0.2, height: size * 0.2, boxShadow: '0 0 0 4px var(--bg-elevated)' }}
      >
        <div className="rounded-full" style={{ width: size * 0.07, height: size * 0.07, background: '#f5c518' }} />
      </div>
    </div>
  );
}
